import { relayStylePagination } from '@apollo/client/utilities';
import {
  InternalRefetchQueryDescriptor,
  PossibleTypesMap,
  TypePolicies,
  TypePolicy,
} from '@sixfold/app-data-framework';

import { paginationKeyArgs } from './pagination';

export type RefetchQuery = InternalRefetchQueryDescriptor;
export type RefetchQueries = RefetchQuery | InternalRefetchQueryDescriptor[];

export function getRefetchQueriesArray(refetchQueries?: RefetchQueries) {
  if (refetchQueries === undefined) {
    return [];
  }

  return Array.isArray(refetchQueries) ? refetchQueries : [refetchQueries];
}

export const possibleTypes: PossibleTypesMap = {
  CombinedEvent: ['TourEvent', 'StopEvent'],
  TourException: ['DeliveryException', 'ServiceException'],
};

function countedConnectionTypePolicy(fieldName: string): TypePolicy {
  return {
    merge: true,

    fields: {
      [fieldName]: relayStylePagination(paginationKeyArgs),
    },
  };
}

function connectionTypePolicy(): TypePolicy {
  return relayStylePagination(paginationKeyArgs);
}

export const typePolicies: TypePolicies = {
  // Complex types with various field policies
  Company: {
    fields: {
      apiMetricsMessages: connectionTypePolicy(),
      companyRelationships: connectionTypePolicy(),
      providingVisibilityCompanies: { merge: true },
      receivingVisibilityCompanies: { merge: true },
      dataNetworkReport: { merge: true },
      dataNetworkVisibilityReport: { merge: true },
      me: { merge: true },
      tourReportsByCarrier: connectionTypePolicy(),
      tourReportsByShipper: connectionTypePolicy(),
      userGroupsOnPlatform: connectionTypePolicy(),
      relationIdentifiersConnection: connectionTypePolicy(),
      bulkInvitationSet: {
        merge: true,
      },
      latestBulkInvitationSet: {
        merge: true,
      },
      carrierNetwork: { merge: true },
      featureFlagAuditLogConnection: connectionTypePolicy(),
    },
  },

  CompanyPlace: {
    keyFields: ['place_id'],

    fields: {
      possibleAddresses: connectionTypePolicy(),
    },
  },

  CompanyStopAddress: {
    keyFields: ['stopAddressId'],

    fields: {
      closeByAddresses: connectionTypePolicy(),
    },
  },

  Tour: {
    fields: {
      activeVehicleAllocation: { merge: true },
      allocatableVehicles: connectionTypePolicy(),
      stops: { merge: false },
      tourTrafficEntries: connectionTypePolicy(),
      vehicleAllocations: { merge: false },
      cleanedVehicleAllocationHistory: { merge: false },
      activeAllocations: { merge: false },
      vehicleHistory: { merge: false },
      oceanVehicleHistory: { merge: false },
      warnings: { merge: false },
    },
  },

  TourStop: { keyFields: ['stop_id'] },

  SharedViewBrandingAssets: { keyFields: false },
  SharedViewBrandingAsset: { keyFields: false },
  CompanySharedViewBranding: { keyFields: false },
  UploadSharedViewLogoResult: { keyFields: false },
  ResendSharedViewEmailInvitationResult: { keyFields: false },

  // Bulk archive/restore return a plain result summary (no identifiable id) shared by both mutations.
  ArchiveCarrierNetworkItemsResult: { keyFields: false },
  ArchiveVehiclesResult: { keyFields: false },
  BulkTourUserAssignmentResult: { keyFields: false },
  DedicatedVehiclesSummaryCounts: { keyFields: false },

  DeliverySettings: {
    keyFields: false,
  },

  ExternalEmissions: {
    keyFields: false,
  },

  UserGroupOnPlatform: {
    keyFields: ['groupId'],

    fields: {
      users: connectionTypePolicy(),
    },
  },

  TourVisibilityPerformance: {
    keyFields: false,
    fields: {
      tourMetrics: connectionTypePolicy(),
    },
  },

  VehicleHistoryDownload: { keyFields: false },
  VehicleHistoryDownloadRow: { keyFields: false },

  OceanMilestonePerformanceResult: { keyFields: false },
  OceanMilestonePerformanceRow: { keyFields: false },
  OceanLocationTypeCount: { keyFields: false },
  OceanPortOption: { keyFields: false },

  ResolvedDateTimeRange: { keyFields: false },

  VisibilityPerformanceOverview: {
    keyFields: false,
    merge: true,
    fields: {
      companies: connectionTypePolicy(),
    },
  },

  VisibilityPerformanceOverviewConnection: {
    fields: {
      companies: connectionTypePolicy(),
    },
  },

  // Counted connection types
  CountedBulkInvitationSetConnection: countedConnectionTypePolicy('bulkInvitationSets'),
  CountedBulkInvitationSetItemConnection: countedConnectionTypePolicy('items'),
  CountedCarrierConnection: countedConnectionTypePolicy('carriers'),
  CountedCarrierNetworkConnection: countedConnectionTypePolicy('items'),
  CountedCompanyAnalyticsFilterConnection: countedConnectionTypePolicy('companyAnalyticsFilters'),
  CountedCompanyBookingLocation: countedConnectionTypePolicy('bookingLocations'),
  CountedCompanyPlaceConnection: countedConnectionTypePolicy('places'),
  CountedCompanyRelationConnection: countedConnectionTypePolicy('networkRelations'),
  CountedCompanyStopAddressConnection: countedConnectionTypePolicy('addresses'),
  CountedCompanyStopCitiesConnection: countedConnectionTypePolicy('cityIdentifiers'),
  CountedCompanyStopPostalCodesConnection: countedConnectionTypePolicy('postalCodeIdentifiers'),
  CountedCompanyStopStatesConnection: countedConnectionTypePolicy('stopStatesIdentifiers'),
  CountedCompanyTourFilterConnection: countedConnectionTypePolicy('companyTourFilters'),
  CountedCompanyTransportGroupConnection: countedConnectionTypePolicy('companyTransportGroups'),
  CountedCompanyTransportNetworkFilterConnection: countedConnectionTypePolicy('companyTransportNetworkFilters'),
  CountedCustomFieldValueConnection: countedConnectionTypePolicy('values'),
  CountedDashboardsConnection: countedConnectionTypePolicy('dashboards'),
  CountedDataNetworkVisibilityReportCompanyConnection: countedConnectionTypePolicy('companies'),
  CountedIncotermConnection: countedConnectionTypePolicy('incoterms'),
  CountedMainCarrierConnection: countedConnectionTypePolicy('mainCarriers'),
  CountedPlaceTourConnection: countedConnectionTypePolicy('tours'),
  CountedProvidingVisibilityCompaniesConnection: countedConnectionTypePolicy('companies'),
  CountedReceivingVisibilityCompaniesConnection: countedConnectionTypePolicy('companies'),
  CountedShipperConnection: countedConnectionTypePolicy('shippers'),
  CountedSiteConnection: countedConnectionTypePolicy('sites'),
  CountedTelematicsIntegrationConnection: countedConnectionTypePolicy('integrations'),
  CountedTourConnection: countedConnectionTypePolicy('tours'),
  CountedOceanIdentifiersConnection: countedConnectionTypePolicy('tourIdentifiers'),
  CountedOceanLocationsOptionConnection: countedConnectionTypePolicy('options'),
  CountedOceanTourConnection: countedConnectionTypePolicy('tours'),
  CountedTripsIdentifiersConnection: countedConnectionTypePolicy('tourIdentifiers'),
  CountedTripsOptionConnection: countedConnectionTypePolicy('options'),
  CountedTripsTourConnection: countedConnectionTypePolicy('tours'),
  CountedUserFilterConnection: countedConnectionTypePolicy('userFilters'),
  CountedUserInCompanyProfileConnection: countedConnectionTypePolicy('users'),
  CountedVehicleConnection: countedConnectionTypePolicy('vehicles'),
  CountedVehicleGrantPartnerConnection: countedConnectionTypePolicy('partners'),
  CountedVehicleInGroupConnection: countedConnectionTypePolicy('vehicles'),
  CountedWidgetsConnection: countedConnectionTypePolicy('widgets'),
  CountedOrderItemConnection: countedConnectionTypePolicy('orderItemIdentifiers'),
  CountedOrderItemCustomFieldConnection: countedConnectionTypePolicy('orderItemCustomFieldIdentifiers'),
  CountedStopCompanyNameConnection: countedConnectionTypePolicy('stopCompanyNameIdentifiers'),
  CountedTelematicsProviderConnection: countedConnectionTypePolicy('providers'),
  CountedCarrierNetworkStopNameConnection: countedConnectionTypePolicy('carrierNetworkStopNameIdentifiers'),
  CountedCustomerConnection: countedConnectionTypePolicy('customers'),
  CountedSharedViewConnection: countedConnectionTypePolicy('sharedViews'),
  CountedCompanyStopGateIdentifiersConnection: countedConnectionTypePolicy('stopGateIdentifiers'),
  // Non-counted connection types
  CarrierReportConnection: connectionTypePolicy(),
  TelematicsProviderConnection: connectionTypePolicy(),
  ShipperReportConnection: connectionTypePolicy(),
  VehicleProfileConnection: connectionTypePolicy(),
  VisibilityPerformanceOverviewRowConnection: connectionTypePolicy(),

  // Edge types are not normalized (i.e they should only appear on the connection they appear on)
  ApiMetricsEntryEdge: { keyFields: false },
  CarrierNetworkItemEdge: { keyFields: false },
  CarrierReportEdge: { keyFields: false },
  CompanyAnalyticsFilterEdge: { keyFields: false },
  CompanyBookingLocationEdge: { keyFields: false },
  CompanyPlaceEdge: { keyFields: false },
  CompanyProfileEdge: { keyFields: false },
  CarrierOptionEdge: { keyFields: false },
  CompanyStopAddressEdge: { keyFields: false },
  CompanyStopCitiesEdge: { keyFields: false },
  CompanyStopPostalCodesEdge: { keyFields: false },
  CompanyStopStatesEdge: { keyFields: false },
  CompanyTourFilterEdge: { keyFields: false },
  CompanyTransportNetworkFilterEdge: { keyFields: false },
  CustomFieldValueEdge: { keyFields: false },
  DashboardEdge: { keyFields: false },
  DataNetworkVisibilityReportCompanyEdge: { keyFields: false },
  ShipperReportEdge: { keyFields: false },
  SiteEdge: { keyFields: false },
  TelematicsProviderEdge: { keyFields: false },
  TourEdge: { keyFields: false },
  TourMetricsListRowEdge: { keyFields: false },
  TripsOptionEdge: { keyFields: false },
  TripsTourIdentifierEdge: { keyFields: false },
  UserFilterEdge: { keyFields: false },
  UserInCompanyProfileEdge: { keyFields: false },
  VehicleEdge: { keyFields: false },
  VehicleGrantPartnerEdge: { keyFields: false },
  VehicleProfileEdge: { keyFields: false },
  WidgetEdge: { keyFields: false },
  ProvidingVisibilityCompanyEdge: { keyFields: false },
  ReceivingVisibilityCompanyEdge: { keyFields: false },
  NetworkRelationEdge: { keyFields: false },
  BulkInvitationSetItemEdge: { keyFields: false },
  OrderItemIdentifierEdge: { keyFields: false },
  OrderItemCustomFieldEdge: { keyFields: false },
  StopCompanyNameEdge: { keyFields: false },
  VisibilityPerformanceOverviewRowEdge: { keyFields: false },
  StopCompanyName: { keyFields: false },
  SharedViewEdge: { keyFields: false },
  InitiateUploadResult: { keyFields: false },
  CompanyInvitationRecipient: { keyFields: false },
  InvitationHistoryItem: { keyFields: false },
  InvitationHistoryItemRecipient: { keyFields: false },
  GeofenceZone: { keyFields: false },
  Geofence: { keyFields: false },
  CompanyIdentifierEdge: { keyFields: false },
  BulkInvitationIdentifierValue: { keyFields: false },
  ValidatedCreateBulkInvitationSetItem: { keyFields: false },
  CompanyStopGateIdentifierEdge: { keyFields: false },

  // Types we do not want to normalise, but don't mind merging
  // (i.e child types that can be combined between separate requests)
  Address: { keyFields: false, merge: true },
  ActualTimeOverrides: { keyFields: false, merge: true },
  ApiEndpointUsageLog: { keyFields: false, merge: true },
  ApiMetricsEntry: { keyFields: false, merge: true },
  DocumentCreator: { keyFields: false, merge: true },
  ApplicationDeepLink: { keyFields: false, merge: true },
  BreakCenterPosition: { keyFields: false, merge: true },
  Bounds: { keyFields: false, merge: true },
  CarrierNetworkStatusContext: { keyFields: false, merge: true },
  CircleGeofence: { keyFields: false, merge: true },
  CompanyAddress: { keyFields: false, merge: true },
  CompanyAssets: { keyFields: false, merge: true },
  CompanyBookingLocation: { keyFields: false, merge: true },
  CompanyCountBySignupStatus: { keyFields: false, merge: true },
  CompanyIdAndVisibilityServices: { keyFields: false, merge: true },
  CompanyInviteMetadata: { keyFields: false, merge: true },
  CompanyInviteRecipient: { keyFields: false, merge: true },
  CompanySettings: { keyFields: false, merge: true },
  CompanyStopCities: { keyFields: false, merge: true },
  CompanyStopPostalCodes: { keyFields: false, merge: true },
  CompanyStopStates: { keyFields: false, merge: true },
  CompanyVatNumber: { keyFields: false, merge: true },
  CompanyVehicles: { keyFields: false, merge: true },
  CompanyWebhookSubscription: { keyFields: false, merge: true },
  CompanyTransportContactDetails: { keyFields: false, merge: true },
  ConnectedCustomField: { keyFields: false, merge: true },
  ConnectionParameter: { keyFields: false, merge: true },
  ConnectionParameterEnumValue: { keyFields: false, merge: true },
  CustomField: { keyFields: false, merge: true },
  DateTimeRange: { keyFields: false, merge: true },
  DateTimeWithTimezone: { keyFields: false, merge: true },
  DelayTimeRangeOutput: { keyFields: false, merge: true },
  EndpointUsageCounterByDate: { keyFields: false, merge: true },
  GeoPosition: { keyFields: false, merge: true },
  LicensePlateLookupResultSet: { keyFields: false, merge: true },
  LicensePlateLookupResult: { keyFields: false, merge: true },
  LicensePlateLookupVehicle: { keyFields: false, merge: true },
  LocationsAndMilestones: { keyFields: false, merge: true },
  MaritimeAffectedPort: { keyFields: false, merge: true },
  MilestoneWithLocation: { keyFields: false, merge: true },
  MilestoneDelayOutput: { keyFields: false, merge: true },
  MilestoneLocation: { keyFields: false, merge: true },
  OceanMilestones: { keyFields: false, merge: true },
  OceanRoute: { keyFields: false, merge: true },
  OceanRouteLeg: { keyFields: false, merge: true },
  OceanDemurrage: { keyFields: false, merge: true },
  OceanDemurrageCost: { keyFields: false, merge: true },
  OceanCostDetails: { keyFields: false, merge: true },
  OrderItemCustomField: { keyFields: false, merge: true },
  TransportAlert: { keyFields: false, merge: true },
  OnboardingAction: { keyFields: false, merge: true },
  PageInfo: { keyFields: false, merge: true },
  PlaceGeofence: { keyFields: false, merge: true },
  PlaceGeofenceWithZone: { keyFields: false, merge: true },
  PointLocation: { keyFields: false, merge: true },
  PolygonGeofence: { keyFields: false, merge: true },
  Polyline: { keyFields: false, merge: true },
  Position: { keyFields: false, merge: true },
  RestrictedVehicleStatus: { keyFields: false, merge: true },
  Route: { keyFields: false, merge: true },
  RouteLeg: { keyFields: false, merge: true },
  RouteLegMetadata: { keyFields: false, merge: true },
  SharedCompanyTourFilter: { keyFields: false, merge: true },
  SharedViewConsignee: { keyFields: false, merge: true },
  SharedNotificationReport: { keyFields: false, merge: true },
  SharedTourDelivery: { keyFields: false, merge: true },
  SharedTourStop: { keyFields: false, merge: true },
  SimpleTimeslot: { keyFields: false, merge: true },
  StopAllocation: { keyFields: false, merge: true },
  StopContact: { keyFields: false, merge: true },
  TelematicsValidation: { keyFields: false, merge: true },
  TemperatureSensor: { keyFields: false, merge: true },
  TemperatureSensorReading: { keyFields: false, merge: true },
  TimeRange: { keyFields: false, merge: true },
  Timeslot: { keyFields: false, merge: true },
  TourAssignment: { keyFields: false, merge: true },
  TokenBrandingAssets: { keyFields: false, merge: true },
  TokenResources: { keyFields: false, merge: true },
  TourCalculatedEmissions: { keyFields: false, merge: true },
  TourCO2Emissions: { keyFields: false, merge: true },
  TourEmissionsReceived: { keyFields: false, merge: true },
  FuelBasedEmissions: { keyFields: false, merge: true },
  TourCountByFailureReason: { keyFields: false, merge: true },
  TourCustomFieldSearchType: { keyFields: false, merge: true },
  TourCustomFieldSearchValueType: { keyFields: false, merge: true },
  TourFailureReason: { keyFields: false, merge: true },
  TourFilter: { keyFields: false, merge: true },
  TourLocation: { keyFields: false, merge: true },
  ToursCountByDelayStatus: { keyFields: false, merge: true },
  ToursCountByStatus: { keyFields: false, merge: true },
  TourSignal: { keyFields: false, merge: true },
  TourVisibilitySource: { keyFields: false, merge: true },
  TourWarning: { keyFields: false, merge: true },
  TrackingNotStarted: { keyFields: false, merge: true },
  TrackingNotStartedRemainingTime: { keyFields: false, merge: true },
  TransportNetworkCustomFieldSearchType: { keyFields: false, merge: true },
  TransportNetworkCustomFieldSearchValueType: { keyFields: false, merge: true },
  TransportNetworkFilter: { keyFields: false, merge: true },
  TourIncoterms: { keyFields: false, merge: true },
  TripsOption: { keyFields: false, merge: true },
  TripsTourIdentifier: { keyFields: false, merge: true },
  UnifiedCompanyOnboardingEvent: { keyFields: false, merge: true },
  UserInCompany: { keyFields: false, merge: true },
  UserInCompanyProfile: { keyFields: false, merge: true },
  UserInCompanyConfiguration: { keyFields: false, merge: true },
  VehicleAllocation: { keyFields: false, merge: true },
  VehicleBreakHistoryEntry: { keyFields: false, merge: true },
  VehicleData: { keyFields: false, merge: true },
  VehicleGrantPartner: { keyFields: false, merge: true },
  VehicleHistoryPolyline: { keyFields: false, merge: true },
  VehicleHistoryPolylineMetadata: { keyFields: false, merge: true },
  VehicleChangePosition: { keyFields: false, merge: true },
  VehiclePosition: { keyFields: false, merge: true },
  VehicleProfile: { keyFields: false, merge: true },
  VehiclePermissions: { keyFields: false, merge: true },
  VehicleStatus: { keyFields: false, merge: true },
  VehicleTemperature: { keyFields: false, merge: true },
  VehicleTemperatureOutOfRange: { keyFields: false, merge: true },
  VehicleTracker: { keyFields: false, merge: true },
  VehicleAlternativeIdentifierConflict: { keyFields: false, merge: true },
  VesselRoutePolyline: { keyFields: false, merge: true },
  VesselPosition: { keyFields: false, merge: true },
  MobileVehicleTracker: { keyFields: ['vehicleTrackerId'], merge: true },
  VisibilityIssue: { keyFields: false, merge: true },
  WidgetFilter: { keyFields: false, merge: true },
  WidgetPreview: { keyFields: false, merge: true },
  OceanLocationsOption: { keyFields: false, merge: true },
  OceanLocationsOptionEdge: { keyFields: false, merge: true },
  OceanTransportLocationOutput: { keyFields: false, merge: true },
  TransportLocationOutput: { keyFields: false, merge: true },
  OceanTourIdentifier: { keyFields: false, merge: true },
  OceanTourIdentifierEdge: { keyFields: false, merge: true },
  OceanDemurrageFilterOutput: { keyFields: false, merge: true },
  TransportAlertsFilterOutput: { keyFields: false, merge: true },
  LocationMilestoneOutput: { keyFields: false, merge: true },
  LocationMilestoneTimelineEventOutput: { keyFields: false, merge: true },
  TimeRangeSugarDateOutput: { keyFields: false, merge: true },
  ReceivingVisibilityStatistics: { keyFields: false, merge: true },
  NetworkRelationStatistics: { keyFields: false, merge: true },
  CompanyTransportGroup: { keyFields: false, merge: true },
  CompanyTransportGroupEdge: { keyFields: false, merge: true },
  NotificationTrigger: { keyFields: false, merge: true },
  NotificationCustomization: { keyFields: false, merge: true },
  EtaComparison: { keyFields: false, merge: true },
  Recipient: { keyFields: false, merge: true },
  ImpactDaysOutput: { keyFields: false, merge: true },
  FinalizeTransportUploadResult: { keyFields: false, merge: true },
  InitiateTransportUploadResult: { keyFields: false, merge: true },
  CreateCompanyInvitationsResult: { keyFields: false, merge: true },
  CreateBulkInvitationSetResult: { keyFields: false, merge: true },
  UpdateBulkInvitationSetResult: { keyFields: false, merge: true },
  DeleteBulkInvitationSetResult: { keyFields: false, merge: true },
  DeleteInvitationsResult: { keyFields: false, merge: true },
  MoveItemsToBulkInvitationSetResult: { keyFields: false, merge: true },
  AddItemsToBulkInvitationSetResult: { keyFields: false, merge: true },
  SendBulkInvitationSetResult: { keyFields: false, merge: true },
  CreateCompanyInvitationLinkResult: { keyFields: false, merge: true },
  CreateCompanyInvitationLinkWithRecipientsResult: { keyFields: false, merge: true },
  CreateCompanyInvitationEmailResult: { keyFields: false, merge: true },
  CompanyVatNumberChangeResult: { keyFields: false, merge: true },
  SetVehicleAlternativeIdentifiersResult: { keyFields: false, merge: true },
  SetBulkVehicleAlternativeIdentifiersResult: { keyFields: false, merge: true },
  BulkVehicleAlternativeIdentifiersItemResult: { keyFields: false, merge: true },
  BulkInvitationMetadata: { keyFields: false, merge: true },
  BulkInvitationSetStatistics: { keyFields: false, merge: true },
  ItemCountByStatus: { keyFields: false, merge: true },
  BulkInvitationRecipient: { keyFields: false, merge: true },
  VehicleCountByStatus: { keyFields: false, merge: true },
  WithdrawCompanyInvitationResult: { keyFields: false, merge: true },
  CreateOrUpdateRelationIdentifiersResult: { keyFields: false, merge: true },
  OrderItemIdentifier: { keyFields: false, merge: true },
  StopCompanyNameIdentifiers: { keyFields: false, merge: true },
  UpdateUserNotificationConfigResponse: { keyFields: false, merge: true },
  LastSignalTimestampFilter: { keyFields: false, merge: true },
  TemperatureEverOutOfRangeFilter: { keyFields: false, merge: true },
  CompanyFilterBasedNotifications: { keyFields: false, merge: true },
  TourEventMetadata: { keyFields: false, merge: true },
  StopEventMetadata: { keyFields: false, merge: true },
  TourFailureReasonMetadata: { keyFields: false, merge: true },
  VesselNameFilterOutput: { keyFields: false, merge: true },
  OrderItemCustomFieldOutput: { keyFields: false, merge: true },
  TripVesselNames: { keyFields: false, merge: true },
  ExceptionFile: { keyFields: false, merge: true },
  RelatedTransportForListView: { keyFields: false, merge: true },
  StopFilterOutput: { keyFields: false, merge: true },
  TourTemperatureAsset: { keyFields: false, merge: true },
  TourAdditionalAsset: { keyFields: false, merge: true },
  TourVehicleAllocation: { keyFields: false, merge: true },
  IntegrationSyncResult: { keyFields: false, merge: true },
  VisibilityPerformanceOverviewMetrics: { keyFields: false, merge: true },
  VehicleHistoryCustomer: { keyFields: false, merge: true },
  VehicleStatusMetainfo: { keyFields: false, merge: true },
  TourTrackingMetadata: { keyFields: false, merge: true },
  VisibilityPerformanceFilter: { keyFields: false, merge: true },
  GeoJSONGeometry: { keyFields: false, merge: true },
  PositionGeoJSONFeatureCollection: { keyFields: false, merge: true },
  PositionGeoJSONFeature: { keyFields: false, merge: true },
  PositionGeoJSONProperties: { keyFields: false, merge: true },
  CreateVisibilityAgentConversationResult: { keyFields: false, merge: true },
  SendVisibilityAgentMessageResult: { keyFields: false, merge: true },
  SubmitVisibilityAgentFeedbackResult: { keyFields: false, merge: true },
  VisibilityAgentFeedbackResult: { keyFields: false, merge: true },
  VisibilityAgentResponseMetadata: { keyFields: false, merge: true },
  VisibilityAgentResponseError: { keyFields: false, merge: true },
  VisibilityAgentValidationError: { keyFields: false, merge: true },
  VisibilityAgentConversationsResponse: { keyFields: false, merge: true },
  VisibilityAgentMessageMetadata: { keyFields: false, merge: true },
  VisibilityAgentPageInfo: { keyFields: false, merge: true },
  StopVisited: { keyFields: false, merge: true },
  TourFilterOptions: { keyFields: false, merge: true },
  NotesFilterOptions: { keyFields: false, merge: true },
  StopStateOptions: { keyFields: false, merge: true },
  StopStateOptionPage: { keyFields: false, merge: true },
  StopStateOption: { keyFields: false, merge: true },
  StopCityOptions: { keyFields: false, merge: true },
  StopCityOptionPage: { keyFields: false, merge: true },
  StopCityOption: { keyFields: false, merge: true },
  StopPostalCodeOptions: { keyFields: false, merge: true },
  StopPostalCodeOptionPage: { keyFields: false, merge: true },
  StopPostalCodeOption: { keyFields: false, merge: true },
  StopCompanyNameOptions: { keyFields: false, merge: true },
  StopCompanyNameOptionPage: { keyFields: false, merge: true },
  StopCompanyNameOption: { keyFields: false, merge: true },

  // Types that just have a custom key field
  SharedViewContent: { keyFields: ['sharedViewId'] },
  SharedViewViewer: { keyFields: ['viewerId'] },
  CarrierNetworkItem: { keyFields: ['entityId'] },
  Dashboard: { keyFields: ['dashboardId'] },
  CarrierReport: { keyFields: ['carrier', ['company_id']] },
  CombinedEvent: { keyFields: ['event_id'] },
  CompanyAsset: { keyFields: ['url'] },
  CompanyOverview: { keyFields: ['companyId'] },
  CompanyPlaceAddress: { keyFields: ['addressId'] },
  CompanyPlatform: { keyFields: ['platform_id', 'company_id_on_platform'] },
  CompanyProfile: { keyFields: ['company_id'] },
  CarrierOption: { keyFields: ['company_id'] },
  CompanySignupCompleted: { keyFields: ['companySignupId'] },
  CompanyTelematicsIntegration: { keyFields: ['telematicsIntegrationId'] },
  CustomFieldValue: { keyFields: ['value'] },
  DataNetworkVisibilityReportCompany: { keyFields: ['companyId'] },
  ExternalTourDeliveryEvent: { keyFields: ['eventId'] },
  ExternalTourEvent: { keyFields: ['eventId'] },
  ExternalTourStopEvent: { keyFields: ['eventId'] },
  MaritimeIntelligenceEvent: { keyFields: ['eventId'] },
  ShipperReport: { keyFields: ['shipper', ['company_id']] },
  StopEvent: { keyFields: ['event_id'] },
  StopEventV2: { keyFields: ['eventId'] },
  TokenBrandingAsset: { keyFields: ['url'] },
  TrackedVehicle: { keyFields: ['vehicleId'] },
  TransportUpload: { keyFields: ['documentId'] },
  TransportUploadUploader: { keyFields: ['userId'] },
  TourDelivery: { keyFields: ['deliveryId'] },
  TourEvent: { keyFields: ['event_id'] },
  TourMetricsListRow: { keyFields: ['tour_id'] },
  UserOnPlatform: { keyFields: ['userIdOnPlatform'] },
  UserProfile: { keyFields: ['user_id'] },
  VehicleBreakHistory: { keyFields: ['breakId'] },
  VehicleGrant: { keyFields: ['grantId'] },
  VehicleTrackerTour: { keyFields: ['tour_id'] },
  VehicleTrackerTourStop: { keyFields: ['stop_id'] },
  ProvidingVisibilityCompany: { keyFields: ['companyId'] },
  ReceivingVisibilityCompany: { keyFields: ['companyId'] },
  FilterBasedTransportNotification: { keyFields: ['notificationId'] },
  NetworkRelation: { keyFields: ['entityId', 'type'] },
  BulkInvitationSetItem: { keyFields: ['bulkInvitationSetItemId'] },
  TourDeliveryException: { keyFields: ['exceptionId'] },
  TourException: { keyFields: ['exceptionId'] },
  DeliveryException: { keyFields: ['exceptionId'] },
  ServiceException: { keyFields: ['exceptionId'] },
  CompanyIdentifier: { keyFields: ['companyIdentifierId'] },
  SharingToken: { keyFields: ['token'] },
  SharedViewEmailAccess: { keyFields: ['sharedViewAccessId'] },
  SharedViewEmailAccessToken: { keyFields: ['tokenId'] },
  AllocationReference: { keyFields: ['allocationId'] },
  ConflictingLicensePlate: { keyFields: ['licensePlate'] },
  ConflictingRemoteId: { keyFields: ['remoteId'] },
  NotificationUserProfile: { keyFields: ['userId'] },
  RelationIdentifier: { keyFields: ['companyIdentifierId', 'identifierValue'] },
  RelationIdentifierWithValidation: { keyFields: ['companyIdentifierId', 'identifierValue'] },
  TransportAnalysisResponse: { keyFields: ['analyzeId'] },
  VisibilityPerformanceOverviewRow: {
    keyFields: (object) => {
      // Company-aggregated rows have a stable identity, so normalize them by companyId.
      if (object.companyId) {
        return String(object.companyId);
      }
      // Other aggregations (by failure reason, by time bucket, ...) are identified only
      // by `key` (e.g. ['true'], ['false']), which is independent of the query filter.
      // Do NOT normalize them: a shared key lets a filtered query overwrite an unfiltered
      // query's counts (e.g. the cancellation-reasons denominator flipping to 100%).
      // Returning false keeps each query's rows embedded under its own filter-keyed field.
      return false;
    },
  },
  SharedViewUserProfile: { keyFields: ['userId'] },
  SharedViewLink: { keyFields: ['token'] },
  SharedViewDeprecated: { keyFields: ['sharedViewId'] },
  VisibilityAgentConversation: { keyFields: ['conversationId'] },
  VisibilityAgentConversationMessages: { keyFields: ['conversationId'] },
  VisibilityAgentMessage: { keyFields: ['messageId'] },
  TourVehicleLicensePlateFilter: { keyFields: ['plate'] },
  CompanyStopGateIdentifier: { keyFields: ['gateId'] },
};
